/**
 * Sysadmin Audit → Witness Configuration (Phase 5 PR-F v1) — COMPLIANCE-GRADE.
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.7 + W1b-32 background:
 *
 *   This page manages the GLOBAL witness-attestation policy. The W1b-32
 *   audit-witness worker consults this row at every tick (24h by
 *   default) to decide whether to sign + emit a witness record per
 *   tenant.
 *
 *   The actual WORM witness records (audit_witness_records) are
 *   read-only here — they're the tamper-evident facts produced by the
 *   worker. The Witness sub-tab in this file shows them for context
 *   but cannot mutate them.
 *
 * Tamper-evidence invariants (UI-enforced):
 *   - Enable/disable transitions ALWAYS go through TypedConfirmDialog
 *     with the literal confirm phrase "ENABLE WITNESS" or "DISABLE
 *     WITNESS". Cadence + URL changes use a less rigorous Dialog (no
 *     typed confirm) — they don't mask an evidence gap.
 *   - The Save button is disabled when no fields differ from the
 *     server state. This prevents accidental "Save with no changes"
 *     that would still emit a platform_audit row.
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Eye,
  ShieldCheck,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { StatusPill } from '@/components/ui/status-pill';
import { PageError } from '@/components/ui/page-error';
import { EmptyState } from '@/components/ui/empty-state';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import { StepUpReasonDialog } from '@/components/ui/step-up-reason-dialog';
import {
  useAuditWitnessConfig,
  useUpdateAuditWitnessConfig,
  useAuditWitnessRecords,
  AUDIT_WITNESS_CADENCE_MIN_HOURS,
  AUDIT_WITNESS_CADENCE_MAX_HOURS,
} from '@/lib/use-sysadmin-platform-audit';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/audit/witness')({
  component: WitnessConfigPage,
});

// ─── Page ────────────────────────────────────────────────────────────────

function WitnessConfigPage() {
  const config = useAuditWitnessConfig();
  const records = useAuditWitnessRecords();

  if (config.error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load witness config"
          message={config.error.message}
          onRetry={() => config.refetch()}
          retrying={config.isFetching}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Eye className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Audit Witness
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-xs font-mono">§3.7</Badge>
              <Badge variant="secondary" className="text-xs">PR-F</Badge>
              <Badge variant="outline" className="text-xs font-mono">W1b-32</Badge>
              <span className="text-xs text-muted-foreground">
                External witness URL + cadence + tamper-evident WORM records.
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            config.refetch();
            records.refetch();
          }}
          disabled={config.isFetching || records.isFetching}
        >
          <RefreshCw
            className={cn(
              'h-3.5 w-3.5 mr-1.5',
              (config.isFetching || records.isFetching) && 'animate-spin'
            )}
          />
          Refresh
        </Button>
      </div>

      {config.isLoading ? (
        <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
          Loading witness config…
        </div>
      ) : (
        <>
          <ConfigCard />
          <RecordsCard />
        </>
      )}
    </div>
  );
}

// ─── Config card ─────────────────────────────────────────────────────────

function ConfigCard() {
  const { data } = useAuditWitnessConfig();
  const update = useUpdateAuditWitnessConfig();

  const [enabled, setEnabled] = useState(false);
  const [witnessUrl, setWitnessUrl] = useState('');
  const [witnessPubkeyId, setWitnessPubkeyId] = useState('');
  const [cadence, setCadence] = useState(24);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [enableConfirm, setEnableConfirm] = useState<
    'enabling' | 'disabling' | null
  >(null);
  // Witness config is a tier-1 step-up: after the typed enable/disable confirm
  // (or directly for non-transition edits), collect the audit reason before
  // firing the PATCH (platform audit 2026-06-10 — the prior call sent no reason
  // and 400'd REASON_REQUIRED, so Save never worked).
  const [reasonOpen, setReasonOpen] = useState(false);

  // Hydrate the form from server state on first load and after a
  // successful save. We deliberately do NOT reset on every server poll
  // because the user may be mid-edit; the Save button is the source of
  // truth for "send my changes to the server".
  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setWitnessUrl(data.witnessUrl ?? '');
    setWitnessPubkeyId(data.witnessPubkeyId ?? '');
    setCadence(data.batchCadenceHours);
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  const dirty =
    enabled !== data.enabled ||
    (witnessUrl || null) !== data.witnessUrl ||
    (witnessPubkeyId || null) !== data.witnessPubkeyId ||
    cadence !== data.batchCadenceHours;

  const isEnableTransition = enabled !== data.enabled;
  const cadenceInvalid =
    cadence < AUDIT_WITNESS_CADENCE_MIN_HOURS ||
    cadence > AUDIT_WITNESS_CADENCE_MAX_HOURS;
  const urlInvalid = witnessUrl.length > 0 && !witnessUrl.match(/^https?:\/\//);
  const pubkeyInvalid =
    witnessPubkeyId.length > 0 && !witnessPubkeyId.match(/^[0-9a-f-]{36}$/);

  const canSave =
    dirty && !cadenceInvalid && !urlInvalid && !pubkeyInvalid && !update.isPending;

  const handleSaveAttempt = () => {
    setSaveError(null);
    if (isEnableTransition) {
      setEnableConfirm(enabled ? 'enabling' : 'disabling');
      return;
    }
    void handleSaveConfirmed();
  };

  // After confirmation, collect the step-up reason; the mutation fires from the
  // reason dialog's onConfirm.
  const handleSaveConfirmed = () => {
    setSaveError(null);
    setReasonOpen(true);
  };

  const handleReasonConfirm = (stepUpReason: string) => {
    update.mutate(
      {
        input: {
          enabled,
          witnessUrl: witnessUrl || null,
          witnessPubkeyId: witnessPubkeyId || null,
          batchCadenceHours: cadence,
        },
        stepUpReason,
      },
      {
        onSuccess: () => setReasonOpen(false),
        onError: (err) => {
          setReasonOpen(false);
          setSaveError(err instanceof Error ? err.message : String(err));
        },
      },
    );
  };

  return (
    <SectionCard
      title="Configuration"
      description="The W1b-32 audit-witness worker reads this row at every tick. Changes are tamper-evidence-critical — enable/disable transitions require typed confirmation and are flagged highRiskAction in the audit chain."
    >
      <div className="space-y-4">
        {/* Last status strip */}
        <div className="rounded-md border border-muted-foreground/20 bg-muted/30 p-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">
              <Clock className="h-2.5 w-2.5" />
              Last attempt
            </div>
            <div className="font-mono">
              {data.lastAttemptAt
                ? new Date(data.lastAttemptAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">
              <CheckCircle2 className="h-2.5 w-2.5" />
              Last success
            </div>
            <div className="font-mono">
              {data.lastSuccessAt
                ? new Date(data.lastSuccessAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div className="space-y-0.5">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">
              <AlertTriangle className="h-2.5 w-2.5" />
              Last error
            </div>
            <div className="text-destructive font-mono truncate" title={data.lastError ?? ''}>
              {data.lastError ?? '—'}
            </div>
          </div>
        </div>

        <SettingField
          label="Enabled"
          description="Master switch. When false the worker no-ops every tick."
        >
          <div className="flex items-center gap-3">
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              aria-label="Toggle witness attestation"
            />
            <span className="text-xs font-medium">
              {enabled ? 'On' : 'Off'}
              {isEnableTransition && (
                <span className="ml-2 text-amber-600">
                  (changed — typed-confirm required on Save)
                </span>
              )}
            </span>
          </div>
        </SettingField>

        <SettingField
          label="External witness URL"
          description="Optional. When set, the worker POSTs every record to this URL and marks synced_to_external=true on success. Leave blank for local-only attestation."
        >
          <Input
            placeholder="https://witness.example.com/api/attestations"
            value={witnessUrl}
            onChange={(e) => setWitnessUrl(e.target.value)}
            className="font-mono text-xs"
          />
          {urlInvalid && (
            <p className="text-[10px] text-destructive mt-1">
              Must start with http:// or https://
            </p>
          )}
        </SettingField>

        <SettingField
          label="Witness public-key (KMS key id)"
          description="UUID of the W1b-13 KMS key whose public key the external witness verifies signatures against. Leave blank if not using external witness."
        >
          <Input
            placeholder="00000000-0000-0000-0000-000000000000"
            value={witnessPubkeyId}
            onChange={(e) => setWitnessPubkeyId(e.target.value)}
            className="font-mono text-xs"
          />
          {pubkeyInvalid && (
            <p className="text-[10px] text-destructive mt-1">
              Must be a UUID.
            </p>
          )}
          {data.witnessPubkeyAlias && !pubkeyInvalid && witnessPubkeyId && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Alias: <span className="font-mono">{data.witnessPubkeyAlias}</span>
            </p>
          )}
        </SettingField>

        <SettingField
          label="Batch cadence (hours)"
          description={`How often the witness worker fires. Default 24h. Range ${AUDIT_WITNESS_CADENCE_MIN_HOURS}–${AUDIT_WITNESS_CADENCE_MAX_HOURS}.`}
        >
          <Input
            type="number"
            min={AUDIT_WITNESS_CADENCE_MIN_HOURS}
            max={AUDIT_WITNESS_CADENCE_MAX_HOURS}
            value={cadence}
            onChange={(e) => setCadence(Number(e.target.value))}
            className="font-mono w-32"
          />
          {cadenceInvalid && (
            <p className="text-[10px] text-destructive mt-1">
              Must be between {AUDIT_WITNESS_CADENCE_MIN_HOURS} and{' '}
              {AUDIT_WITNESS_CADENCE_MAX_HOURS} hours.
            </p>
          )}
        </SettingField>

        {saveError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            {saveError}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || update.isPending}
            onClick={() => {
              setEnabled(data.enabled);
              setWitnessUrl(data.witnessUrl ?? '');
              setWitnessPubkeyId(data.witnessPubkeyId ?? '');
              setCadence(data.batchCadenceHours);
              setSaveError(null);
            }}
          >
            Discard
          </Button>
          <Button onClick={handleSaveAttempt} disabled={!canSave}>
            {update.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Typed-confirm for enable/disable transitions */}
      {enableConfirm === 'enabling' && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setEnableConfirm(null)}
          title="Enable audit witness"
          description="This enables tamper-evident attestation. The worker will sign each tenant's audit chain head every cadence cycle. Required for ITAR / CMMC L2 compliance."
          consequences={[
            'A platform_audit row will be written (highRiskAction=true).',
            'The worker will begin signing chain heads on the next tick.',
            'Every change to this config is itself recorded in the audit chain.',
          ]}
          confirmPhrase="ENABLE WITNESS"
          confirmLabel="Enable witness"
          variant="warning"
          onConfirm={async () => {
            setEnableConfirm(null);
            await handleSaveConfirmed();
          }}
        />
      )}

      {enableConfirm === 'disabling' && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setEnableConfirm(null)}
          title="Disable audit witness"
          description="Disabling witness attestation removes the tamper-evidence layer. The audit_chain still records events, but no external proof of integrity will be generated. ITAR / CMMC L2 deployments MUST NOT disable witness."
          consequences={[
            'A platform_audit row will be written (highRiskAction=true).',
            'New witness records will stop being created at the next tick.',
            'Existing WORM records remain in audit_witness_records (cannot be deleted).',
            'Compliance audits may fail if disabled during a regulated period.',
          ]}
          confirmPhrase="DISABLE WITNESS"
          confirmLabel="Disable witness"
          variant="danger"
          onConfirm={async () => {
            setEnableConfirm(null);
            await handleSaveConfirmed();
          }}
        />
      )}

      {reasonOpen && (
        <StepUpReasonDialog
          open
          onOpenChange={(o) => !o && setReasonOpen(false)}
          title="Audit reason for witness config change"
          description="Tier-1 step-up. Witness config drives the external tamper-evidence pipeline. State the audit reason for this change."
          submitLabel={update.isPending ? 'Saving…' : 'Save configuration'}
          submitting={update.isPending}
          onConfirm={handleReasonConfirm}
        />
      )}
    </SectionCard>
  );
}

// ─── Records card ────────────────────────────────────────────────────────

function RecordsCard() {
  const { data, isLoading, error } = useAuditWitnessRecords();

  if (error) {
    return (
      <SectionCard
        title="Recent witness records"
        description="WORM facts — one row per tenant per tick. Cannot be edited or deleted (enforced by DB trigger)."
      >
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          Failed to load: {error.message}
        </div>
      </SectionCard>
    );
  }

  const items = data?.items ?? [];

  return (
    <SectionCard
      title="Recent witness records"
      description="Tamper-evident attestations produced by the audit-witness worker. WORM-enforced — UPDATE/DELETE attempts raise a Postgres exception. Cross-check against the Integrity sub-tab."
    >
      {isLoading && items.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
          Loading witness records…
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No witness records yet"
          body="The worker hasn't produced any records yet. Witness records appear on the next tick after witness is enabled and a tenant chain has events."
        />
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Witnessed at</th>
                <th className="px-3 py-2 font-medium">Tenant</th>
                <th className="px-3 py-2 font-medium text-right">Seq</th>
                <th className="px-3 py-2 font-medium">Hash</th>
                <th className="px-3 py-2 font-medium">External</th>
                <th className="px-3 py-2 font-medium">Pubkey</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t hover:bg-muted/30">
                  <td className="px-3 py-2 text-xs font-mono">
                    {new Date(r.witnessedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono" title={r.organizationId}>
                    {r.organizationId.slice(0, 8)}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-right">
                    {r.chainHeadSeq}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono" title={r.chainHeadHash}>
                    {r.chainHeadHash.slice(0, 16)}…
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.syncedToExternal ? (
                      <StatusPill status="healthy" compact icon={Link2}>
                        Synced
                      </StatusPill>
                    ) : (
                      <StatusPill status="muted" compact>
                        Local
                      </StatusPill>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono" title={r.witnessPubkeyId}>
                    {r.witnessPubkeyId.slice(0, 8)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
